import moment from "moment";
import { useGrowl } from "PFApp/use_growl";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useStorage from "PFCore/helpers/use_storage";
import { useSupplyStatsQuery } from "PFCore/hooks/queries/bookings/supply_reports/use_supply_stats_query";
import { useSupplyUtilizationQuery } from "PFCore/hooks/queries/bookings/supply_reports/use_supply_utilization_query";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { FeatureFlag } from "PFTypes";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFlattenedAvailabilityFilters } from "../../../utils/use_flattened_availability_filters";
import { formatVisibleTiles } from "../../../utils/utils";
import { DateGranularity } from "../../common/date_granularity_selector";
import { VisibleCategory } from "../../types/types";
import FiltersBookingModule from "../filters_booking_module";
import TilesSection from "./charts/tiles_section/tiles_section";
import UtilizationChart from "./charts/utilization_chart";

export enum Utilization {
  Total = "total",
  Billable = "billable"
}

const SupplySection = (): JSX.Element => {
  const { t } = useTranslation("insights");
  const growl = useGrowl();
  const showUtilizationMetrics = useIsFeatureEnabled()(FeatureFlag.ShowUtilization);

  const { formatISODate } = useDateFormatter();
  const [visibleTiles, setVisibleTiles] = useStorage<VisibleCategory[]>(`bookingInsightsVisibleTiles`, []);

  const defaultFilters = {
    availability: {
      start: formatISODate(),
      end: formatISODate(moment().add(3, "months"))
    }
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [utilizationOption, setUtilizationOption] = useState<Utilization>(Utilization.Total);
  const [dateGranularity, setDateGranularity] = useState(DateGranularity.daily);
  const flattenedFilters = useFlattenedAvailabilityFilters(filters);

  const {
    data: statsData,
    isLoading: isStatsLoading,
    error: statsError
  } = useSupplyStatsQuery(
    {
      params: {
        ...flattenedFilters
      }
    },
    {
      onError: () => {
        growl({
          message: t("error"),
          kind: "error"
        });
      }
    }
  );

  const {
    data: graphData,
    isLoading: isLoadingUtilization,
    error: utilizationError
  } = useSupplyUtilizationQuery(
    {
      params: {
        date_granularity: dateGranularity,
        ...flattenedFilters
      }
    },
    {
      enabled: showUtilizationMetrics,
      onError: () => {
        growl({
          message: t("error"),
          kind: "error"
        });
      }
    }
  );

  const visibleCategories = useMemo(
    () => formatVisibleTiles(statsData?.total_stats.categories, visibleTiles),
    [statsData?.total_stats.categories, visibleTiles]
  );

  return (
    <>
      <FiltersBookingModule
        setFilters={setFilters}
        defaultFilters={defaultFilters}
        setUtilization={setUtilizationOption}
        utilizationOption={utilizationOption}
        visibleCategories={visibleCategories}
        setVisibleCategories={setVisibleTiles}
        tab={"supply"}
      />
      {statsData && (
        <TilesSection
          data={{ ...statsData?.total_stats, categories: visibleCategories }}
          utilizationOption={utilizationOption}
          loading={isStatsLoading}
          error={!!statsError}
        />
      )}
      {showUtilizationMetrics && (
        <UtilizationChart
          utilizationData={graphData?.utilisation_graph || []}
          utilizationOption={utilizationOption}
          loading={isLoadingUtilization}
          dateGranularity={dateGranularity}
          setDateGranularity={setDateGranularity}
          error={!!utilizationError}
        />
      )}
    </>
  );
};

export default SupplySection;
